/**
 * COMMON API ENDPOINTS
 */

import commonApi from './Common.api';
import reduxStore from './../reduxStore';
import xhr from './../utils/xhr';
import utils from './../utils/utilities';

import { LOCALES, DEFAULT_LANGUAGE, consoleSettings } from './../constants';

import {
    UPDATE_USER_PREFERENCES,
    UPDATE_PENDING_UPLOADS,
    USER_PRODUCT_AUTHORIZATIONS
} from './../pages/Main/Main.reducers';

const actions = {
    updateUserPrefs: function(prefs) {
        reduxStore.dispatch({
            type:    UPDATE_USER_PREFERENCES,
            payload: prefs
        });
        return commonApi.updateUserPrefs(reduxStore.getState().user.preferences);
    },

    updateUserFilters: function(filterData) {
        let oldFilters = reduxStore.getState().user.preferences.filters;
        let newFilters = Object.assign({}, oldFilters, filterData);
        return actions.updateUserPrefs({filters: newFilters});
    },

    updateDashboardPrefs: function(dashboardData) {
        let oldPrefs = reduxStore.getState().user.preferences.dashboard;
        let newPrefs = Object.assign({}, oldPrefs, dashboardData);
        return actions.updateUserPrefs({dashboard: newPrefs});
    },

    updatePendingUploads: function (pendingUpload){
        reduxStore.dispatch({
            type: UPDATE_PENDING_UPLOADS,
            payload: pendingUpload
        });
    },

    loadPageMeta() {
        let promises = [];
        promises.push(
            xhr.get({url: '/login/helpUrlRoot'})
                .then((response) => {
                    reduxStore.dispatch({
                        type:    'SET_HELP_URL_ROOT',
                        payload: response.text
                    })
                }));

        promises.push(
            xhr.get({url: '/login/version'})
                .then((response) => {
                    reduxStore.dispatch({
                        type:    "SET_VERSION",
                        payload: response.text
                    });}
                ));

        promises.push(
            xhr.get({url: '/login/settings'})
                .then((response)=>{
                    reduxStore.dispatch({
                        type:    "USER_AUTHENTICATION_TYPE",
                        payload: response.body
                    });
                }));

        promises.push(actions.fetchIntlTranslations());

        return promises;
    },

    loadPageProfile() {
        let promises = [];

        promises.push(
            xhr.get({url: '/api/user.json'})
                .then((response) => {
                    // Successfully fetched user data and present the start screen (either Batch Upload or Dashboard)
                    reduxStore.dispatch(
                        {
                            type: 'USER_INFO_SUCCESS',
                            payload: response.body
                        }
                    );
                    reduxStore.dispatch({
                        type: 'EXPERIAN_FLAG',
                        payload: response.body.experianEnabled
                    });
                }));

        promises.push(
            xhr.get({url: '/api/user/authorizations'})
                .then(response => {
                //Succesfully fetched user authorizations
                reduxStore.dispatch(
                    {
                        type: USER_PRODUCT_AUTHORIZATIONS,
                        payload: response.body
                    }
                );
            }));

        promises.push(
            xhr.get({ url: '/api/system/pguidTranslations' })
                .then((response) => {
                    let pguids = response.body,
                        pguidsMap = {};

                    for (let i = 0; i < pguids.length; i++) {
                        pguidsMap[pguids[i].pguid] = pguids[i];
                        let def = pguids[i]['en_us'];
                        if (!def || def === '--') {
                            LOCALES['en-us'].translations['pguidTranslation_' +
                            pguids[i].pguid] = LOCALES['en-us'].translations['General_CoreFunctionality_UIText_general.notAvailableShort'];
                            LOCALES['en-gb'].translations['pguidTranslation_' +
                            pguids[i].pguid] = LOCALES['en-gb'].translations['General_CoreFunctionality_UIText_general.notAvailableShort'];
                            LOCALES['fr'].translations['pguidTranslation_' +
                            pguids[i].pguid] = LOCALES['fr'].translations['General_CoreFunctionality_UIText_general.notAvailableShort'];
                            LOCALES['de'].translations['pguidTranslation_' +
                            pguids[i].pguid] = LOCALES['de'].translations['General_CoreFunctionality_UIText_general.notAvailableShort'];
                            continue;
                        }
                        LOCALES['en-us'].translations['pguidTranslation_' + pguids[i].pguid] = def;
                        if (!pguids[i]['en_gb'] || pguids[i]['en_gb'] === '--') {
                            LOCALES['en-gb'].translations['pguidTranslation_' + pguids[i].pguid] = def;
                        } else {
                            LOCALES['en-gb'].translations['pguidTranslation_' + pguids[i].pguid] = pguids[i]['en_gb'];
                        }
                        if (!pguids[i]['de'] || pguids[i]['de'] === '--') {
                            LOCALES['de'].translations['pguidTranslation_' + pguids[i].pguid] = def;
                        } else {
                            LOCALES['de'].translations['pguidTranslation_' + pguids[i].pguid] = pguids[i]['de'];
                        }
                        if (!pguids[i]['fr'] || pguids[i]['fr'] === '--') {
                            LOCALES['fr'].translations['pguidTranslation_' + pguids[i].pguid] = def;
                        } else {
                            LOCALES['fr'].translations['pguidTranslation_' + pguids[i].pguid] = pguids[i]['fr'];
                        }
                    }

                    reduxStore.dispatch({
                        type: 'SET_PGUID_TRANSLATIONS',
                        payload: pguidsMap
                    });
            }));
        return promises;
    },


    /**
     * Fetches translations from database and updates app's default translations
     * @return {Promise}
     */
    fetchIntlTranslations() {
        let intlTranslations = LOCALES;
        let requestedKeys = [],
            checkedKeys = {},
            languagesDefaultCheck = {};

        for (let language in intlTranslations) {
            languagesDefaultCheck[language] = false;
        }

        for (let key in intlTranslations[DEFAULT_LANGUAGE].translations) {
            requestedKeys.push(key);
            checkedKeys[key] = Object.assign({}, languagesDefaultCheck);
        }

        return xhr.post({
            url: '/login/translations',
            data: requestedKeys
        }).then((response) => {
            const t = response.body;
            // For each translation language
            for (let language in intlTranslations) {
                // For each translation key(tKey) in received DB Translations(t), build translation object
                for (let tKey in t) {
                    if (t[tKey][intlTranslations[language].dbKey] && t[tKey][intlTranslations[language].dbKey].trim().length) {
                        intlTranslations[language].translations[tKey] = t[tKey][intlTranslations[language].dbKey];
                        checkedKeys[tKey][language] = true;
                    }
                }
            }

            let missingKeys = {};

            // Check if all keys and language translation from database exist
            for (let key in checkedKeys) {
                for (let language in checkedKeys[key]) {
                    if (!checkedKeys[key][language]) {
                        if (!missingKeys[key]) {
                            missingKeys[key] = []
                        }
                        missingKeys[key].push(language);
                        intlTranslations[language].translations[key] = intlTranslations[DEFAULT_LANGUAGE].translations[key]
                    }
                }
            }
            if (!utils.isEmptyObject(missingKeys)) {
                console.warn('%c !!! MISSING TRANSLATIONS !!! ', consoleSettings.style.failure)
                console.log(missingKeys)
            } else {
                console.info('%c <<< TRANSLATIONS :: ALL OK >>>', consoleSettings.style.sucess)
            }
        }).catch((error) => {
            // if call fails altogether, use default language for all LOCALES
            for (let language in intlTranslations) {
                if (language !== DEFAULT_LANGUAGE) {
                    intlTranslations[language].translations = intlTranslations[DEFAULT_LANGUAGE].translations
                }
            }
            utils.logError(error, 'index.fetchIntlTranslations(): ');
        });
    }


};

export default actions;


// WEBPACK FOOTER //
// ./src/scripts/api/Common.actions.js